import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoTopicGaps, seoTopicGapAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Stat, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/topic-gaps")({
  head: () => ({ meta: [{ title: "Topic gaps · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: TopicGapsPage,
});

function TopicGapsPage() {
  const load = useServerFn(seoTopicGaps);
  const act = useServerFn(seoTopicGapAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-topic-gaps"], queryFn: () => load() });
  const rows = ((q.data as any)?.rows ?? []) as any[];

  const refresh = useMutation({
    mutationFn: () => act({ data: { action: "refresh" } }),
    onSuccess: (r: any) => {
      toast.success(`${r.gaps} topics queued${r.fromSearch ? " (including live search data)" : ""}.`);
      qc.invalidateQueries({ queryKey: ["seo-topic-gaps"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not rebuild topic gaps."),
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: "planned" | "dismissed" }) => act({ data: { action: "status", ...v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo-topic-gaps"] }),
  });

  return (
    <SeoPage
      title="Topic gaps"
      description="Articles our loan-product and tool clusters still need, questions we already get shown for without a page that answers them, and subjects competitors cover that we do not."
      actions={
        <Button onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          {refresh.isPending ? "Rebuilding…" : "Rebuild topic list"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Open topics" value={num(rows.length)} />
            <Stat label="High priority" value={num(rows.filter((r) => r.priority <= 1).length)} />
            <Stat label="Also covered by competitors" value={num(rows.filter((r) => (r.competitor_domains ?? []).length > 0).length)} />
          </div>
          <Panel title="Topics to write" subtitle="Marking one as planned hands it to the blog planner, which still drafts only — nothing publishes itself.">
            {rows.length === 0 ? (
              <Empty>No gaps queued yet. Rebuild the list to check.</Empty>
            ) : (
              <DataTable
                head={["Topic", "Priority", "Supports", "Why", "Competitors", ""]}
                rows={rows.map((r) => [
                  r.topic_label,
                  <Pill key={r.id} tone={r.priority <= 1 ? "bad" : r.priority === 2 ? "warn" : "neutral"}>{`P${r.priority}`}</Pill>,
                  r.our_path ?? "—",
                  r.notes ?? "—",
                  (r.competitor_domains ?? []).join(", ") || "—",
                  <div key={`${r.id}-b`} className="flex gap-2">
                    <Button size="sm" onClick={() => setStatus.mutate({ id: r.id, status: "planned" })}>
                      Plan it
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: r.id, status: "dismissed" })}>
                      Dismiss
                    </Button>
                  </div>,
                ])}
              />
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
